import { createRouter, createWebHistory, type RouteRecordRaw, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/store/AuthStore';
import { AppType, UserRole } from '@/types/auth.types';

// =============================================================================
// Route Meta Types
// =============================================================================

declare module 'vue-router' {
    interface RouteMeta {
        /** Route requires authentication */
        requiresAuth?: boolean;
        /** Route requires access to specific app */
        requiresApp?: AppType;
        /** Route requires minimum role level */
        requiresMinRole?: UserRole;
        /** Page title for browser tab */
        title?: string;
        /** Icon for navigation */
        icon?: string;
        /** Hide from navigation */
        hideInNav?: boolean;
        /** Hide the shell top bar (logo + user menu) for this route */
        hideTopBar?: boolean;
    }
}

// =============================================================================
// Auth Routes
// =============================================================================

const authRoutes: RouteRecordRaw[] = [
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: {
            requiresAuth: false,
            title: 'Login',
            hideInNav: true,
        },
    },
];

// =============================================================================
// Fidelity System Routes
// =============================================================================

const fidelityRoutes: RouteRecordRaw[] = [
    {
        path: '/fidelity',
        name: 'Fidelity',
        redirect: '/fidelity/dashboard',
        component: () => import('@/views/artisans/HomeView.vue'),
        meta: {
            requiresAuth: true,
            requiresApp: AppType.FIDELITY,
            title: 'Fidelity System',
        },
        children: [
            {
                path: 'dashboard',
                name: 'FidelityDashboard',
                component: () => import('@/views/artisans/dashboard/DashboardView.vue'),
                meta: {
                    title: 'Dashboard',
                    icon: 'dashboard',
                },
            },
            {
                path: 'rewards',
                name: 'Rewards',
                component: () => import('@/views/artisans/rewards/RewardsView.vue'),
                meta: {
                    title: 'Rewards',
                    icon: 'rewards',
                },
            },
            {
                path: 'scans',
                name: 'Scans',
                component: () => import('@/views/artisans/scans/ScansView.vue'),
                meta: {
                    title: 'Scans',
                    icon: 'scans',
                },
            },
            {
                path: 'scans/:scanId',
                name: 'ScanDetail',
                component: () => import('@/views/artisans/scans/ScanDetailView.vue'),
                meta: {
                    title: 'Scan Details',
                    hideInNav: true,
                },
            },
            {
                path: 'claims',
                name: 'Claims',
                component: () => import('@/views/artisans/claims/ClaimsView.vue'),
                meta: {
                    title: 'Claims',
                    icon: 'claims',
                },
            },
            {
                path: 'influencers',
                name: 'Influencers',
                component: () => import('@/views/artisans/referral/InfluencersView.vue'),
                meta: {
                    title: 'Influencers',
                    icon: 'influencers',
                },
            },
            {
                path: 'influencers/:influencerId',
                name: 'InfluencerDetail',
                component: () => import('@/views/artisans/referral/InfluencerDetailView.vue'),
                meta: {
                    title: 'Influencer Details',
                    hideInNav: true,
                },
            },
            {
                path: 'settings',
                name: 'FidelitySettings',
                component: () => import('@/views/artisans/AppSettings.vue'),
                meta: {
                    title: 'Settings',
                    icon: 'settings',
                    requiresMinRole: UserRole.SUPER_ADMIN,
                },
            },
        ],
    },
];

// =============================================================================
// SAV System Routes
// =============================================================================

const savRoutes: RouteRecordRaw[] = [
    {
        path: '/sav',
        name: 'SAV',
        redirect: '/sav/dashboard',
        component: () => import('@/views/sav/SAVHomeView.vue'),
        meta: {
            requiresAuth: true,
            requiresApp: AppType.SAV,
            title: 'SAV System',
        },
        children: [
            {
                path: 'dashboard',
                name: 'SAVDashboard',
                component: () => import('@/views/sav/dashboard/SAVDashboard.vue'),
                meta: {
                    title: 'Dashboard',
                    icon: 'dashboard',
                },
            },
            {
                path: 'reports',
                name: 'SAVReports',
                component: () => import('@/views/sav/reports/SAVReportsView.vue'),
                meta: {
                    title: 'Reports',
                    icon: 'reports',
                },
            },
            {
                path: 'reports/:reportId',
                name: 'SAVReportDetail',
                component: () => import('@/views/sav/reports/SAVReportDetailView.vue'),
                meta: {
                    title: 'Report Details',
                    hideInNav: true,
                    hideTopBar: true,
                },
            },
            {
                path: 'identity',
                name: 'SAVIdentity',
                component: () => import('@/views/sav/identityValidation/SAVIdentityValidationView.vue'),
                meta: {
                    title: 'Identity Validation',
                    icon: 'identity',
                },
            },
            {
                path: 'users',
                name: 'SAVUsers',
                component: () => import('@/views/sav/users/SAVUsersManagement.vue'),
                meta: {
                    title: 'Users',
                    icon: 'users',
                },
            },
            {
                path: 'tickets',
                name: 'SAVTickets',
                component: () => import('@/views/sav/tickets/SAVTicketsScreen.vue'),
                meta: {
                    title: 'Tickets',
                    icon: 'tickets',
                },
            },
            {
                path: 'settings',
                name: 'SAVSettings',
                component: () => import('@/views/sav/SAVAppSettings.vue'),
                meta: {
                    title: 'Settings',
                    icon: 'settings',
                    requiresMinRole: UserRole.SUPER_ADMIN,
                },
            },
        ],
    },
];

// =============================================================================
// Admin System Routes (User Management - Super Admin only)
// =============================================================================

const adminRoutes: RouteRecordRaw[] = [
    {
        path: '/admin',
        name: 'Admin',
        redirect: '/admin/dashboard',
        component: () => import('@/views/admin/AdminHomeView.vue'),
        meta: {
            requiresAuth: true,
            requiresMinRole: UserRole.SUPER_ADMIN,
            title: 'Admin',
        },
        children: [
            {
                path: 'dashboard',
                name: 'AdminDashboard',
                component: () => import('@/views/admin/dashboard/AdminDashboard.vue'),
                meta: {
                    title: 'Dashboard',
                    icon: 'dashboard',
                },
            },
            {
                path: 'users',
                name: 'AdminUsers',
                component: () => import('@/views/admin/users/AdminUsersView.vue'),
                meta: {
                    title: 'Utilisateurs',
                    icon: 'users',
                },
            },
            {
                path: 'products',
                name: 'AdminProducts',
                component: () => import('@/views/admin/products/AdminProductsView.vue'),
                meta: {
                    title: 'Produits',
                    icon: 'products',
                },
            },
            {
                path: 'brands',
                name: 'AdminBrands',
                component: () => import('@/views/admin/brands/AdminBrandsView.vue'),
                meta: {
                    title: 'Marques',
                    icon: 'brands',
                },
            },
        ],
    },
];

// =============================================================================
// App Selector Route (for users with access to both apps)
// =============================================================================

const appSelectorRoutes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'AppSelector',
        component: () => import('@/views/auth/AppSelectorView.vue'),
        meta: {
            requiresAuth: true,
            title: 'Select Application',
        },
    },
];

// =============================================================================
// 404 Route
// =============================================================================

const errorRoutes: RouteRecordRaw[] = [
    {
        path: '/403',
        name: 'Forbidden',
        component: () => import('@/views/auth/ForbiddenView.vue'),
        meta: {
            title: 'Access Denied',
            hideInNav: true,
        },
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/views/auth/NotFoundView.vue'),
        meta: {
            title: 'Not Found',
            hideInNav: true,
        },
    },
];

// =============================================================================
// Router Instance
// =============================================================================

const router = createRouter({
    history: createWebHistory(),
    routes: [
        ...authRoutes,
        ...appSelectorRoutes,
        ...fidelityRoutes,
        ...savRoutes,
        ...adminRoutes,
        ...errorRoutes,
    ],
});

// =============================================================================
// Navigation Guards
// =============================================================================

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const authStore = useAuthStore();

    // Initialize auth if not done
    if (!authStore.isInitialized) {
        await authStore.initialize();
    }

    // Update page title
    const title = to.meta.title ? `${to.meta.title} | Vinko` : 'Vinko Dashboard';
    document.title = title;

    // Check if route requires authentication
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth !== false);

    // If requires auth and not authenticated, redirect to login
    if (requiresAuth && !authStore.isAuthenticated) {
        return next({
            name: 'Login',
            query: { redirect: to.fullPath },
        });
    }

    // If already authenticated and going to login, redirect to home
    if (to.name === 'Login' && authStore.isAuthenticated) {
        return next({ name: 'AppSelector' });
    }

    // Single-app users have nothing to select — skip the app selector
    // and land directly in their app (super admin always sees the selector)
    if (to.name === 'AppSelector' && authStore.isAuthenticated && !authStore.isSuperAdmin) {
        const apps = authStore.user?.apps ?? [];
        if (apps.length === 1) {
            return next({ path: apps[0] === AppType.SAV ? '/sav' : '/fidelity' });
        }
    }

    // Check app access
    const requiresApp = to.matched.find(record => record.meta.requiresApp)?.meta.requiresApp;
    if (requiresApp && !authStore.canAccessApp(requiresApp)) {
        console.warn(`Access denied to app: ${requiresApp}`);
        return next({ name: 'Forbidden' });
    }

    // Check minimum role
    const requiresMinRole = to.meta.requiresMinRole;
    if (requiresMinRole && !authStore.hasMinRole(requiresMinRole)) {
        console.warn(`Access denied. Required minimum role: ${requiresMinRole}`);
        return next({ name: 'Forbidden' });
    }

    next();
});

export default router;

// =============================================================================
// Helper: Get navigable routes for an app
// =============================================================================

export function getNavigableRoutes(app: AppType): RouteRecordRaw[] {
    const authStore = useAuthStore();
    const appRoutes = app === AppType.FIDELITY ? fidelityRoutes : savRoutes;

    if (!appRoutes[0]?.children) return [];

    return appRoutes[0].children.filter(route => {
        // Hide routes marked as hideInNav
        if (route.meta?.hideInNav) return false;

        // Check minimum role if required
        const requiredMinRole = route.meta?.requiresMinRole;
        if (requiredMinRole && !authStore.hasMinRole(requiredMinRole)) {
            return false;
        }

        return true;
    });
}
